import React, { useContext, useEffect, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Button, Dimmer, Icon, Loader, Popup, Table } from 'semantic-ui-react';

import '../css/LtiContentPickerView.less';

import { register } from '../i18n';
import { DIALOG_NAMES } from '../managers/DialogManager';
import { VIEW_SELECTION } from '../managers/NavigationManager';
import { SatCoreComponent, SatCoreRegister } from '../SatCoreRegistry';
import LtiService from '../services/LtiService';
import PopupService from '../services/PopupService';
import UtilityService from '../services/UtilityService';

const t = register('LtiContentPickerView');

/**
 * Check if element delivery mode allows student assignment
 */
const isStudentAssignable = (deliveryMode) => {
  return deliveryMode && (deliveryMode.indexOf('assign') > -1 || deliveryMode === 'student_always');
};

const LtiContentPickerView = observer(({ history }) => {
  const { assignmentManager, contentManager, dialogManager, ltiManager, navigationManager, tagContentManager } = useContext(MobXProviderContext);

  const IndeterminateCheckbox = SatCoreComponent('IndeterminateCheckbox');
  const LTIPreview = SatCoreComponent('LTIPreview');
  const PopupButton = SatCoreComponent('PopupButton');

  const [isLoadingLtiContentPickerCourses, setIsLoadingLtiContentPickerCourses] = useState(false);
  const [isLoadingMoreLtiContentPickerTableData, setIsLoadingMoreLtiContentPickerTableData] = useState(false);
  const [isLoadingGoToSearch, setIsLoadingGoToSearch] = useState(false);
  const [isSubmittingLtiContentPickerSelections, setIsSubmittingLtiContentPickerSelections] = useState(false);
  const [previewCourseElement, setPreviewCourseElement] = useState(null);

  useEffect(() => {
    navigationManager.setView(VIEW_SELECTION.LTI_CONTENT_PICKER);
  }, []);

  useEffect(() => {
    const loadCourses = async () => {
      // Only fetch if we do not already have courses in MobX
      if (ltiManager.ltiTableRowCourseIds.length === 0) {
        setIsLoadingLtiContentPickerCourses(true);
        try {
          await LtiService.loadContentPickerCourses(ltiManager.ltiClassroomId);
        } catch (error) {
          console.error('Error loading courses:', error);
        } finally {
          setIsLoadingLtiContentPickerCourses(false);
        }
      }
    };

    loadCourses();
  }, [ltiManager.ltiClassroomId]);

  // ============================================================================
  // LTI CONTENT PICKER HANDLERS
  // ============================================================================
  /**
   * Handle add content button click
   */
  const handleAddContent = async () => {
    const selections = ltiManager.collectLtiContentPickerSelections();

    if (selections.length === 0) {
      dialogManager.setOpenDialog(DIALOG_NAMES.TEXT, {
        message: t('noContentSelected'),
        size: 'tiny',
        title: t('noContentSelectedTitle')
      },
      () => dialogManager.closeDialog(DIALOG_NAMES.TEXT));
      return;
    }

    await submitLtiContentPickerSelectionsAndClose(selections);
  };

  /**
   * Navigate to LTI content picker search view.
   * Clears any picker selections so search starts with a clean selection state.
   */
  const navigateToSearch = () => {
    ltiManager.clearLtiContentPickerSelections();
    navigationManager.setView(VIEW_SELECTION.LTI_CONTENT_PICKER_SEARCH);
    history.push(`/ltiContentPickerSearch?view=${VIEW_SELECTION.LTI_CONTENT_PICKER_SEARCH}`);
  };

  /**
   * Handle Go to Search button - warn if selections exist, then navigate to search view
   */
  const handleGoToSearch = async () => {
    if (isLoadingGoToSearch) {
      return;
    }

    setIsLoadingGoToSearch(true);

    try {
      const entityTypeIds = 'assessment,lesson,article_resource,audio_resource,bookmark_resource,file_resource,'
        + 'image_resource,video_resource,learnosity_activity_resource,google_doc_resource,google_sheet_resource,'
        + 'google_slide_resource,pdf_resource';
      await tagContentManager.fetchAvailableCategoriesWithTags(entityTypeIds, 'COURSE');

      if (ltiManager.hasLtiContentPickerSelections()) {
        dialogManager.setOpenDialog(DIALOG_NAMES.CONFIRM, {
          cancelButtonName: t('goToSearchCancel'),
          confirmButtonName: t('goToSearchContinue'),
          confirmHandler: () => {
            dialogManager.closeDialog(DIALOG_NAMES.CONFIRM);
            navigateToSearch();
          },
          message: t('goToSearchMessage'),
          title: t('goToSearchTitle')
        },
        () => dialogManager.closeDialog(DIALOG_NAMES.CONFIRM));
      } else {
        navigateToSearch();
      }
    } catch (error) {
      console.error('Error loading search filters:', error);
    } finally {
      setIsLoadingGoToSearch(false);
    }
  };

  // ============================================================================
  // LTI CONTENT PICKER LAUNCHERS
  // ============================================================================

  /**
   * Add a single assignment.
   * - LTI 1.3: submit a single-element deep-link selection and let the LMS close the popup.
   * - LTI 1.0/1.1 (e.g. EdLink): call the legacy endpoint and postMessage the parent.
   */
  const addAssignment = async (elementId, studentReview) => {
    const row = ltiManager.ltiTableRowMap.get(elementId);

    if (!row) {
      console.error('Row not found for elementId:', elementId);
      return;
    }

    if (ltiManager.isLti13) {
      await submitLtiContentPickerSelectionsAndClose([{
        addToCourse: true,
        allowStudentReview: !!studentReview,
        createAssignment: true,
        elementId
      }]);
      return;
    }

    const { contentItemId, entityId, courseContentItemId } = row;
    const classroomId = ltiManager.ltiClassroomId;

    try {
      await LtiService.addLtiAssignment(
        contentItemId || entityId,
        elementId,
        classroomId,
        courseContentItemId,
        studentReview
      );

      const assignmentData = assignmentManager.getLastAddedAssignment();
      window.parent.postMessage({
        custom: {
          contentItemId: assignmentData.contentItemId,
          courseResourceElementId: assignmentData.courseResourceElementId,
          subdomainLinkId: assignmentData.subdomainLinkId
        },
        type: 'lti'
      }, '*');
    } catch (error) {
      dialogManager.setOpenDialog(DIALOG_NAMES.ERROR, {
        errorLabel: t('assignmentErrorTitle'),
        errorMessage: error.message
      },
      () => dialogManager.closeDialog(DIALOG_NAMES.ERROR));
    }
  };

  /**
   * Open Add LTI Assignment modal dialog
   */
  const openAddAssignmentDialog = (elementId) => {
    dialogManager.setOpenDialog(DIALOG_NAMES.ADD_LTI_ASSIGNMENT, {
      addAssignment,
      elementId
    },
    () => dialogManager.closeDialog(DIALOG_NAMES.ADD_LTI_ASSIGNMENT));
  };

  /**
   * Download resource file and open in new tab
   */
  const downloadResource = async (row) => {
    try {
      const downloadUrl = await contentManager.getDownloadResource(row.contentItemId || row.entityId);

      if (downloadUrl && typeof downloadUrl === 'string') {
        window.open(downloadUrl, '_blank');
      }
    } catch (error) {
      console.error('Download failed:', error);
      dialogManager.setOpenDialog(DIALOG_NAMES.ERROR, {
        errorLabel: t('downloadErrorTitle'),
        errorMessage: t('downloadErrorMessage')
      },
      () => dialogManager.closeDialog(DIALOG_NAMES.ERROR));
    }
  };

  // ============================================================================
  // LTI 1.3 DEEP-LINK HELPERS
  // ============================================================================

  /**
   * Submit selections via LTI 1.3 deep-link. On success, show a confirmation dialog
   * and on its dismissal form-POST the deep-link response back to the LMS and call
   * window.close() as a fallback close mechanism.
   */
  const submitLtiContentPickerSelectionsAndClose = async (selections) => {
    setIsSubmittingLtiContentPickerSelections(true);

    try {
      const result = await LtiService.submitContentPickerSelections(
        ltiManager.ltiClassroomId,
        selections,
        ltiManager.ltiJwtToken
      );

      if (result.success) {
        dialogManager.setOpenDialog(DIALOG_NAMES.TEXT, {
          message: t('contentAddedMessage'),
          size: 'tiny',
          title: t('contentAddedTitle')
        },
        () => {
          LtiService.postDeepLinkResponse(result.deeplinkUrl, result.jwt);
          window.close();
        });
      } else {
        dialogManager.setOpenDialog(DIALOG_NAMES.ERROR, {
          errorLabel: t('addContentErrorTitle'),
          errorMessage: t('submissionError')
        },
        () => {
          dialogManager.closeDialog(DIALOG_NAMES.ERROR);
          // TODO uncomment if we want to ask the LMS to close this LTI launch on error.
          // window.parent.postMessage({ subject: 'lti.close' }, '*');
          window.close();
        });
        setIsSubmittingLtiContentPickerSelections(false);
      }
    } catch (error) {
      console.error('Error submitting selections:', error);
      dialogManager.setOpenDialog(DIALOG_NAMES.ERROR, {
        errorLabel: t('addContentErrorTitle'),
        errorMessage: t('submissionError')
      },
      () => {
        dialogManager.closeDialog(DIALOG_NAMES.ERROR);
        // TODO uncomment if we want to ask the LMS to close this LTI launch on error.
        // window.parent.postMessage({ subject: 'lti.close' }, '*');
        window.close();
      });
      setIsSubmittingLtiContentPickerSelections(false);
    }
  };

  // ============================================================================
  // TABLE EVENT HANDLERS
  // ============================================================================

  /**
   * Toggle row expansion and load children
   */
  const toggleRow = async (rowId) => {
    const row = ltiManager.ltiTableRowMap.get(rowId);

    if (!row?.isExpandable) {
      return;
    }

    setIsLoadingMoreLtiContentPickerTableData(true);
    try {
      await LtiService.loadLtiContentPickerChildren(rowId, ltiManager.ltiClassroomId);
    } catch (error) {
      console.error('Error loading children:', error);
    } finally {
      setIsLoadingMoreLtiContentPickerTableData(false);
    }
  };

  /**
   * Open preview modal for course element
   */
  const openPreview = (row) => {
    setPreviewCourseElement(row);
  };

  // ============================================================================
  // TABLE RENDERING
  // ============================================================================

  /**
   * Render complete LTI content picker table
   */
  const renderTable = () => {
    const { ltiTableRowCourseIds } = ltiManager;

    if (isLoadingLtiContentPickerCourses) {
      return <></>;
    }

    if (ltiTableRowCourseIds.length === 0) {
      return <div>{t('noCourses')}</div>;
    }

    return (
      <Table className='LtiContentPickerTable' compact>
        {renderTableHeader()}
        {renderTableBody()}
      </Table>
    );
  };

  /**
   * Render table header row
   */
  const renderTableHeader = () => {
    const { ltiPickerMode } = ltiManager;
    const isContentMode = ltiPickerMode === 'content';

    return (
      <Table.Header className='lti-content-picker-header'>
        <Table.Row>
          <Table.HeaderCell className='cell-content'>{t('content')}</Table.HeaderCell>
          <Table.HeaderCell className='cell-scoring'>{t('scoring')}</Table.HeaderCell>
          <Table.HeaderCell className='cell-delivery-mode'>{t('deliveryMode')}</Table.HeaderCell>
          {!isContentMode && (
            <>
              <Table.HeaderCell className='cell-add-button'>{t('add')}</Table.HeaderCell>
              <Table.HeaderCell className='cell-preview'>{t('preview')}</Table.HeaderCell>
            </>
          )}
          {isContentMode && (
            <>
              <Table.HeaderCell className='cell-preview'>{t('preview')}</Table.HeaderCell>
              <Table.HeaderCell className='cell-add-to-course'>{t('addToCourse')}</Table.HeaderCell>
              <Table.HeaderCell className='cell-create-assignment'>{t('createAssignment')}</Table.HeaderCell>
              <Table.HeaderCell className='cell-allow-review'>{t('allowReview')}</Table.HeaderCell>
            </>
          )}
        </Table.Row>
      </Table.Header>
    );
  };

  /**
   * Render table body with all course rows
   */
  const renderTableBody = () => {
    const { ltiTableRowCourseIds } = ltiManager;

    return (
      <Table.Body className='lti-content-picker-body'>
        {ltiTableRowCourseIds.map((courseId) => renderTableRow(courseId))}
      </Table.Body>
    );
  };

  /**
   * Render individual table row with recursive children
   */
  const renderTableRow = (rowId) => {
    const CONTENT_EXPANDER_PADDING_PER_LEVEL = 18;
    const CONTENT_EXPANDER_PADDING_BASE = 0;

    const row = ltiManager.ltiTableRowMap.get(rowId);

    if (!row) {
      return null;
    }

    const { ltiPickerMode } = ltiManager;
    const isContentMode = ltiPickerMode === 'content';

    const {
      ltiTableRowExpandedIdSet,
      selectedLtiContentPickerAddToCourseIds,
      selectedLtiContentPickerCreateAssignmentIds,
      selectedLtiContentPickerAllowStudentReviewIds
    } = ltiManager;

    const contentExpanderPadding = CONTENT_EXPANDER_PADDING_BASE + (row.depth * CONTENT_EXPANDER_PADDING_PER_LEVEL);
    const contentPopupOffset = [contentExpanderPadding, 0];
    const hasChildren = row.childrenIds && row.childrenIds.length > 0;
    const canExpand = row.isExpandable && (row.childrenIds === null || hasChildren);
    const isExpanded = ltiTableRowExpandedIdSet.has(rowId);

    const { description, deliveryMode, autoScored } = row;
    const contentItemDescription = row.contentItemDescription || '';

    const studentAssignable = isStudentAssignable(deliveryMode);
    const teacherOnly = deliveryMode === 'teacher_only';
    const isDownloadable = LtiService.isLtiDownloadableContent(row);

    const isAddedToCourse = selectedLtiContentPickerAddToCourseIds.has(rowId);
    const hasAssignment = selectedLtiContentPickerCreateAssignmentIds.has(rowId);
    const allowsReview = selectedLtiContentPickerAllowStudentReviewIds.has(rowId);

    const openedIcon = <Icon name='caret down' />;
    const closedIcon = <Icon name='caret right' />;
    const caretIcon = isExpanded ? openedIcon : closedIcon;

    let expandableIconDiv = null;
    if (row.isExpandable) {
      expandableIconDiv = <div className='expandable-icon-div'>{caretIcon}</div>;
    }

    let addToCourseCheckbox = null;
    let assignmentCheckbox = null;
    let studentReviewCheckbox = null;

    const LTI_CHECKBOX_POPUP_OFFSET = [10, 0];
    const LTI_CHECKBOX_POPUP_POSITION = 'top right';

    if (isContentMode) {
      if (studentAssignable || teacherOnly || !deliveryMode) {
        if (!row.isExpandable) {
          const addTooltip = teacherOnly ? t('addToCourseTooltipTeacherOnly') : t('addToCourseTooltip');

          addToCourseCheckbox = PopupService.renderPopup({
            className: 'lti-popup custom-popup zoom-popup',
            content: addTooltip,
            mouseEnterDelay: 1000,
            offset: LTI_CHECKBOX_POPUP_OFFSET,
            position: LTI_CHECKBOX_POPUP_POSITION,
            trigger: (
              <IndeterminateCheckbox
                checked={isAddedToCourse}
                onChange={() => ltiManager.toggleLtiContentPickerAddToCourse(rowId)}
              />
            ),
            wide: false
          });
        }
      }

      const assignmentDisabled = !isAddedToCourse || row.isExpandable;
      if (studentAssignable) {
        const assignmentTooltip = assignmentDisabled ? t('createAssignmentDisabled') : t('createAssignmentTooltip');

        assignmentCheckbox = PopupService.renderPopup({
          className: 'lti-popup custom-popup zoom-popup',
          content: assignmentTooltip,
          mouseEnterDelay: 1000,
          offset: LTI_CHECKBOX_POPUP_OFFSET,
          position: LTI_CHECKBOX_POPUP_POSITION,
          trigger: (
            <IndeterminateCheckbox
              checked={hasAssignment}
              disabled={assignmentDisabled}
              onChange={() => ltiManager.toggleLtiContentPickerCreateAssignment(rowId)}
            />
          ),
          wide: false
        });
      }

      const reviewDisabled = !isAddedToCourse || row.isExpandable;
      if (studentAssignable) {
        const reviewTooltip = reviewDisabled ? t('allowReviewDisabled') : t('allowReviewTooltip');

        studentReviewCheckbox = PopupService.renderPopup({
          className: 'lti-popup custom-popup zoom-popup',
          content: reviewTooltip,
          mouseEnterDelay: 1000,
          offset: LTI_CHECKBOX_POPUP_OFFSET,
          position: LTI_CHECKBOX_POPUP_POSITION,
          trigger: (
            <IndeterminateCheckbox
              checked={allowsReview}
              disabled={reviewDisabled}
              onChange={() => ltiManager.toggleLtiContentPickerAllowStudentReview(rowId)}
            />
          ),
          wide: false
        });
      }
    }

    let addButton = null;
    if (!isContentMode && studentAssignable && !row.isExpandable) {
      addButton = (
        <PopupButton
          basic
          buttonLabel={t('add')}
          onClick={() => openAddAssignmentDialog(rowId)}
          primary />
      );
    }

    let previewButton = null;
    if (!row.isExpandable) {
      if (studentAssignable || (teacherOnly && isContentMode)) {
        if (isDownloadable) {
          previewButton = (
            <PopupButton
              basic
              buttonLabel={t('download')}
              onClick={() => downloadResource(row)}
              primary />
          );
        } else {
          previewButton = (
            <PopupButton
              basic
              buttonLabel={t('preview')}
              onClick={() => openPreview(row)}
              primary />
          );
        }
      }
    }

    let scoring = null;
    if (studentAssignable && !autoScored) {
      scoring = t('manualGrade');
    }

    const deliveryModeText = LtiService.getLtiDeliveryModeText(deliveryMode, t);

    const itemName = UtilityService.reactHtmlParserWrapper(row.name)?.parsed;
    const subtitle = UtilityService.reactHtmlParserWrapper(description || contentItemDescription)?.parsed;

    const popup = (
      <div className='lti-assign-course-label'>
        <div className='lti-assign-course-name'>{itemName}</div>
        {subtitle && <div className='lti-assign-course-description'>{subtitle}</div>}
      </div>
    );

    const label = (
      <div className='lti-assign-course-label'>
        <div className='lti-assign-course-name ellipsis'>{itemName}</div>
        {subtitle && <div className='lti-assign-course-description ellipsis'>{subtitle}</div>}
      </div>
    );

    return (
      <React.Fragment key={rowId}>
        <Table.Row>
          <Table.Cell className='cell-content'>
            <Popup
              className='lti-popup custom-popup zoom-popup'
              content={(<div>{popup}</div>)}
              hoverable
              mouseEnterDelay={1000}
              offset={contentPopupOffset}
              on='hover'
              trigger={(
                <div
                  className='lti-content-cell'
                  onClick={() => toggleRow(rowId)}
                  style={{
                    cursor: canExpand ? 'pointer' : 'default',
                    paddingLeft: contentExpanderPadding
                  }}>
                  {expandableIconDiv}
                  <div className='label-div'>{label}</div>
                </div>
              )}
              wide='very' />
          </Table.Cell>
          <Table.Cell className='cell-scoring'><span>{scoring}</span></Table.Cell>
          <Table.Cell className='cell-delivery-mode'><span>{deliveryModeText}</span></Table.Cell>
          {!isContentMode && (
            <>
              <Table.Cell className='cell-add-button'><span>{addButton}</span></Table.Cell>
              <Table.Cell className='cell-preview'><span>{previewButton}</span></Table.Cell>
            </>
          )}
          {isContentMode && (
            <>
              <Table.Cell className='cell-preview'><span>{previewButton}</span></Table.Cell>
              <Table.Cell className='cell-add-to-course'>{addToCourseCheckbox}</Table.Cell>
              <Table.Cell className='cell-create-assignment'>{assignmentCheckbox}</Table.Cell>
              <Table.Cell className='cell-allow-review'>{studentReviewCheckbox}</Table.Cell>
            </>
          )}
        </Table.Row>
        {isExpanded && hasChildren && row.childrenIds.map((childId) => renderTableRow(childId))}
      </React.Fragment>
    );
  };

  // ============================================================================
  // LtiContentPickerView - MAIN RENDERING
  // ============================================================================

  const { isLtiUnauthorized, ltiPickerMode } = ltiManager;

  if (isLtiUnauthorized) {
    return (
      <div className='LtiContentPickerView'>
        <div className='status-message'>{t('unauthorized')}</div>
      </div>
    );
  }

  const isContentMode = ltiPickerMode === 'content';

  const shouldShowDimmerLoader = (
    isLoadingLtiContentPickerCourses || isLoadingMoreLtiContentPickerTableData || isSubmittingLtiContentPickerSelections
  );

  return (
    <>
      <div className='LtiContentPickerView'>
        <div className='lti-content-picker-view-wrapper'>
          <div className='lti-content-picker-controls'>
            <div className='lti-controls-left'>
              {isContentMode && (
                <Button
                  className='go-to-search-button'
                  loading={isLoadingGoToSearch}
                  onClick={handleGoToSearch}
                  primary>
                  {t('goToSearch')}
                </Button>
              )}
            </div>
            <div className='lti-controls-right'>
              {isContentMode && (
                <Button onClick={handleAddContent} primary>
                  {t('addAllSelectedContent')}
                </Button>
              )}
            </div>
          </div>

          <div className='lti-content-picker-table-wrapper'>
            {renderTable()}
          </div>
        </div>
      </div>

      {previewCourseElement && (
        <LTIPreview
          close={() => setPreviewCourseElement(null)}
          courseElement={previewCourseElement}
        />
      )}

      {(shouldShowDimmerLoader) && (
        <Dimmer active className='lti-content-picker-dimmer' page>
          <Loader />
          {/* <Loader size='big'>{t('loadingContent')}</Loader> */}
        </Dimmer>
      )}
    </>
  );
});

export default withRouter(LtiContentPickerView);
SatCoreRegister('LtiContentPickerView', LtiContentPickerView);
